/* The ONE cart code planner: every discount code the theme puts on the cart goes
 * through here, so two scripts never race on the cart's discount list.
 *
 * ORG-624 per-variant ΕΚΠΤΩΣΗ: a product holding a live vendor-promo SKU (tag
 * no-ekptosi, WORK-244; a permanent offer is not excluded) is out of the
 * automatic customer discounts; its other variants get the tier % from
 * segment-restricted discount codes `EK<set>-<n>` kept by ops
 * (no_ekptosi_tags.py). sections/ekptosi-codes.liquid — rendered server-side
 * with the live cart and customer — says which EK codes the cart needs.
 *
 * ORG-736 gift promos: calls keeps one BXGY code `YG-DORO-…` per rule («buy N
 * inserts of a family, get the holder free»). sections/promo-gift.liquid
 * (snippets/promo-gift-cart) lists the CANDIDATES (each gift code a cart line
 * carries: its need, the value of one free piece, how many of its pieces are in
 * the cart, the insert lines that may feed it, the required blades present), the
 * POOL of insert pieces per line (a box = 10), which insert lines box10.js must
 * keep loose (Shopify counts a box line as 1 piece) and the nudges to show.
 * WORK-168/170 (calls 2026-10-19): an insert may feed several codes, and the codes
 * combine, so allocate() shares the pool out — the most valuable candidate first —
 * and EVERY earned code is applied. Shopify then never gives a piece both a gift
 * and another product %, and ops keeps the tier % off gift-promo products while
 * the promo runs, so there is no «tier is worth more» case any more.
 *
 * After every cart change: ?sections=ekptosi-codes,promo-gift → Box10.normalize
 * with the hold list (it may rewrite lines: then wait for the next cart:updated)
 * → /cart/update.js {discount}. Shopify takes at most 5 codes: the gift codes
 * first (most valuable first), then the EK codes saving the most; codes the customer typed (neither
 * EK… nor YG-DORO-…) are kept and count toward the 5.
 * Loaded for a logged-in tier customer who is not staff (window.CUSTOMER_DISC.codes)
 * and, while a gift promo is live, for every visitor, staff too (window.PROMO_GIFT;
 * layout/theme.liquid; WORK-233: staff-order.js reads the free pieces off the lines). A code Shopify would not apply is
 * harmless: it just gives nothing. */
(function (root) {
  var MAX_CODES = 5;
  var EK = /^EK\d+[A-Z]?-\d+$/;
  var GIFT = /^YG-DORO-[A-Z0-9-]+$/;

  function up(code) { return String(code || '').toUpperCase(); }
  function isEk(code) { return EK.test(up(code)); }
  function isGift(code) { return GIFT.test(up(code)); }
  function isOurs(code) { return isEk(code) || isGift(code); }
  function codesOn(cart) {
    return ((cart && cart.discount_codes) || []).map(function (d) { return up(d.code || d); });
  }
  function byValue(a, b) { return b.value - a.value || (a.code < b.code ? -1 : 1); }
  // needed: [{code, value}] EK codes from sections/ekptosi-codes; gift: [{code, value}]
  // earned gift codes from sections/promo-gift. → the `discount` string to POST, or
  // null when the cart already carries exactly the codes it should.
  function plan(cart, needed, gift) {
    var on = codesOn(cart);
    var foreign = on.filter(function (c) { return !isOurs(c); });
    var ours = on.filter(isOurs);
    var room = Math.max(0, MAX_CODES - foreign.length);
    var g = (gift || []).filter(function (n) { return n && isGift(n.code) && n.value > 0; }).sort(byValue);
    var ek = (needed || []).filter(function (n) { return n && isEk(n.code) && n.value > 0; }).sort(byValue);
    var want = g.concat(ek).slice(0, room).map(function (n) { return up(n.code); });
    var same = want.length === ours.length && want.every(function (c) { return ours.indexOf(c) > -1; });
    return same ? null : foreign.concat(want).join(',');
  }
  // The ?sections=ekptosi-codes response: {"ekptosi-codes": "<div …><script
  // type=application/json data-ekptosi-codes>[…]</script></div>"}.
  function parseSection(json) {
    var html = json && json['ekptosi-codes'];
    var m = html && /<script[^>]*data-ekptosi-codes[^>]*>([\s\S]*?)<\/script>/.exec(html);
    if (!m) return null;
    try { var a = JSON.parse(m[1]); return Array.isArray(a) ? a : null; } catch (e) { return null; }
  }
  // The ?sections=promo-gift part: {"cands":[{code,need,value,qty,feed:[variant ids],req}],
  // "pool":{variant id: pieces},"hold":[variant ids],"html":"…"} (snippets/promo-gift-cart).
  function parseGift(json) {
    var html = json && json['promo-gift'];
    var m = html && /<script[^>]*data-promo-gift[^>]*>([\s\S]*?)<\/script>/.exec(html);
    if (!m) return null;
    try {
      var o = JSON.parse(m[1]);
      if (!o || !Array.isArray(o.cands) || !o.pool || typeof o.pool !== 'object' || !Array.isArray(o.hold)) return null;
      return {
        cands: o.cands.map(function (c) {
          return { code: c.code, need: c.need, value: c.value, qty: c.qty,
                   feed: (c.feed || []).map(String), req: c.req == null ? null : c.req };
        }),
        pool: o.pool, hold: o.hold.map(String), html: typeof o.html === 'string' ? o.html : '' };
    } catch (e) { return null; }
  }
  // WORK-168/170: share the pool of insert pieces out between the candidate codes.
  // The most valuable free piece is served first; a candidate takes `need` pieces
  // from the lines it accepts (`feed`) per free piece, at most `qty` (its pieces in
  // the cart) and, with `req`, at most the required blades in the cart. → the earned
  // codes [{code, value}], value = what the code saves (cents).
  function allocate(cands, pool) {
    var left = {};
    Object.keys(pool || {}).forEach(function (k) { left[String(k)] = Math.max(0, Math.floor(+pool[k] || 0)); });
    var out = [];
    (cands || []).filter(function (c) { return c && isGift(c.code) && c.need > 0 && c.qty > 0 && c.value > 0; })
      .slice().sort(byValue).forEach(function (c) {
        var feed = (c.feed || []).map(String);
        var have = feed.reduce(function (n, v) { return n + (left[v] || 0); }, 0);
        var free = Math.min(c.qty, Math.floor(have / c.need), c.req == null ? Infinity : Math.max(0, c.req));
        if (!(free > 0)) return;
        var take = free * c.need;
        feed.forEach(function (v) {
          var t = Math.min(take, left[v] || 0);
          left[v] = (left[v] || 0) - t;
          take -= t;
        });
        out.push({ code: up(c.code), value: free * c.value });
      });
    return out;
  }
  // A write is skipped when the same discount was already written for the same
  // lines: a code Shopify drops silently can never start a loop.
  function signature(cart, discount) {
    var lines = ((cart && cart.items) || []).map(function (it) { return it.variant_id + 'x' + it.quantity; }).sort();
    return discount + '|' + lines.join(',');
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { plan: plan, parseSection: parseSection, parseGift: parseGift, allocate: allocate,
      signature: signature, isOurs: isOurs, isEk: isEk, isGift: isGift, MAX_CODES: MAX_CODES };
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  var EK_ON = !!(root.CUSTOMER_DISC && root.CUSTOMER_DISC.codes);
  var GIFT_ON = !!root.PROMO_GIFT;
  if (!EK_ON && !GIFT_ON) return;

  var base = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
  var SECTIONS = ['ekptosi-codes'].concat(GIFT_ON ? ['promo-gift'] : []).join(',');
  var busy = false, dirty = false, lastSig = null;
  var GUARD = 'ekptosi:reloaded';
  function onCartPage() { return /\/cart(\/|$|\?)/.test(root.location.pathname); }
  function store(op, val) {
    try {
      var ss = root.sessionStorage;
      if (!ss) return null;
      if (op === 'get') return ss.getItem(GUARD);
      if (op === 'set') ss.setItem(GUARD, val); else ss.removeItem(GUARD);
    } catch (e) { /* no storage */ }
    return null;
  }

  // What the drawer reads (assets/cart-drawer.js): the held insert lines and the nudge block.
  var gift = { hold: [], html: '' };
  root.PromoGift = {
    held: function (vid) { return gift.hold.indexOf(String(vid)) > -1; },
    html: function () { return gift.html; },
    get hold() { return gift.hold.slice(); }
  };
  function setGift(next) {
    var changed = next.html !== gift.html || next.hold.join() !== gift.hold.join();
    gift = { hold: next.hold, html: next.html };
    if (root.Box10) root.Box10.hold = next.hold;
    if (changed && root.CartDrawer && root.CartDrawer.rerender) root.CartDrawer.rerender();
  }

  // /cart is server-rendered: reload it once per write so its totals show the
  // codes; elsewhere the drawer refresh re-renders (and fires cart:updated,
  // which finds nothing left to do).
  function afterWrite(sig) {
    if (onCartPage()) {
      if (store('get') === sig) return;
      store('set', sig);
      return root.location.reload();
    }
    if (root.CartDrawer) return root.CartDrawer.refresh();
  }
  // Box10 first (gift promo live): with the hold list set it boxes / unboxes what it
  // must; true = it wrote, so the lines are not settled yet.
  function settleBoxes(cart) {
    var B = root.Box10;
    if (!GIFT_ON || !B || !B.whenReady) return Promise.resolve(false);
    return B.whenReady().then(function () { return B.normalize(cart); });
  }
  function cartNow() {
    return root.fetch(base + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('cart.js ' + r.status); return r.json(); });
  }
  function sync(cart) {
    // A change while this sync runs is replayed when it ends (dirty), never dropped.
    if (busy) { dirty = true; return; }
    if (!cart || (root.Box10 && root.Box10.busy)) return;
    var any = (cart.items || []).length > 0;
    if (!any && !codesOn(cart).some(isOurs)) {
      if (GIFT_ON) setGift({ hold: [], html: '' });
      return;
    }
    busy = true;
    var answer = any
      ? root.fetch(base + '?sections=' + SECTIONS, { headers: { 'Accept': 'application/json' } })
          .then(function (r) { if (!r.ok) throw new Error('?sections=' + SECTIONS + ' ' + r.status); return r.json(); })
      : Promise.resolve(null);
    answer.then(function (json) {
      var ek = json ? parseSection(json) : [];
      var g = json ? parseGift(json) : { cands: [], pool: {}, hold: [], html: '' };
      if (ek === null && (!GIFT_ON || g === null)) return;   // unreadable: leave the cart alone
      if (EK_ON && ek === null) return;                       // never drop EK codes on a bad answer
      // Box10 plans absolute quantities: plan boxes and codes from the cart as it is NOW, not
      // the one the sync started with (a tap during the section fetch must not be undone).
      return (GIFT_ON ? cartNow() : Promise.resolve(cart)).then(function (now) { cart = now; })
        .then(function () { if (GIFT_ON && g) setGift(g); return settleBoxes(cart); })
        .then(function (wrote) {
        if (wrote) return;                                     // next cart:updated / box10:idle plans the codes
        var discount = plan(cart, EK_ON ? (ek || []) : [], GIFT_ON && g ? allocate(g.cands, g.pool) : []);
        if (discount === null) { if (onCartPage()) store('remove'); return; }
        var sig = signature(cart, discount);
        if (sig === lastSig) return;
        lastSig = sig;
        return root.fetch(base + 'cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ discount: discount })
        }).then(function (r) {
          if (!r.ok) throw new Error('cart/update.js ' + r.status);
          return afterWrite(sig);
        });
      });
    }).catch(function (err) {
      if (root.console) root.console.error('[CodePlanner]', err);
      // The section failed: boxes still work as before (no hold).
      if (GIFT_ON && root.Box10 && root.Box10.whenReady) {
        root.Box10.hold = [];
        root.Box10.whenReady().then(function () { root.Box10.normalize(cart); });
      }
    }).finally(function () {
      busy = false;
      if (dirty) { dirty = false; setTimeout(syncFresh, 0); }
    });
  }
  function syncFresh() {
    cartNow().then(sync).catch(function (err) { if (root.console) root.console.error('[CodePlanner]', err); });
  }
  document.addEventListener('cart:updated', function (e) { sync(e.detail && e.detail.cart); });
  // Box10 rewrites lines first; the drawer refresh it triggers fires cart:updated while it
  // is still busy (that sync is skipped), so re-read the cart once it is idle.
  document.addEventListener('box10:idle', function () { setTimeout(syncFresh, 0); });
  // No drawer on the page (e.g. checkout-adjacent templates): check once.
  root.addEventListener('DOMContentLoaded', function () {
    if (root.CartDrawer) return;
    syncFresh();
  });
})(typeof window !== 'undefined' ? window : globalThis);
